import React, { useEffect, useState } from "react";
import { Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { fetchAdReport } from "@/lib/api";
import { useAdStrings } from "@/lib/adStrings";
import { DownloadButton, DateFilter } from "@/components/vendor/AdInsights";
import { ShareReportPanel } from "@/components/vendor/ShareReportPanel";
import { EyeInfo, SummaryCards, PinCards } from "@/components/vendor/eye/EyeParts";
import { WeeklyActivity, ReachChart } from "@/components/vendor/eye/EyeCharts";

const NOT_LIVE = ["pending", "pending_approval", "pending_payment", "rejected", "cancelled"];
const iso = (d) => d.toLocaleDateString("en-CA");
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };

function Presets({ adId, preset, pick, E }) {
  const opts = [["all", E.pAll], ["7", E.p7], ["30", E.p30], ["custom", E.pCustom]];
  return (
    <div className="flex gap-1.5 flex-wrap" data-testid={`eye-presets-${adId}`}>
      {opts.map(([k, l]) => (
        <button key={k} type="button" onClick={() => pick(k)} className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${preset === k ? "bg-emerald-700 border-emerald-700 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-emerald-300"}`} data-testid={`eye-preset-${k}-${adId}`}>
          {k === "custom" && <CalendarDays className="inline w-3.5 h-3.5 me-1 -mt-0.5" />}{l}
        </button>
      ))}
    </div>
  );
}

// Vendor Eye report: same recorded data as before, shown as simple cards + charts.
export function VendorAdReport({ adId, canShare = false }) {
  const S = useAdStrings();
  const E = S.eye;
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const [draft, setDraft] = useState({ from: "", to: "" });
  const [preset, setPreset] = useState("all");
  useEffect(() => {
    setErr("");
    let live = true;
    fetchAdReport(adId, range).then((r) => live && setRep(r)).catch((e) => live && setErr(e?.response?.status === 400 ? S.rangeErr : S.loadErr));
    return () => { live = false; };
  }, [adId, range]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (k) => {
    setPreset(k);
    if (k === "all") setRange({ from: "", to: "" });
    else if (k !== "custom") setRange({ from: daysAgo(Number(k) - 1), to: iso(new Date()) });
  };
  const apply = () => { if (draft.from && draft.to && draft.from > draft.to) { toast.error(S.rangeErr); return; } setRange({ ...draft }); };
  if (err && !rep) return <div className="text-xs text-rose-600 mt-2" data-testid={`ad-report-error-${adId}`}>{err}</div>;
  if (!rep) return <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {S.loading}</div>;
  const d = (v) => new Date(v).toLocaleDateString(S.locale, { day: "numeric", month: "short", year: "numeric" });
  const notLive = NOT_LIVE.includes(rep.status);
  const filtered = !!(range.from || range.to);
  const period = filtered ? E.showing(range.from ? d(range.from) : S.start, range.to ? d(range.to) : S.today)
    : rep.period_from ? E.ran(d(rep.period_from), d(rep.period_to)) : E.notStarted;
  return (
    <div className="mt-3 -mx-2 sm:mx-0 rounded-3xl border border-emerald-100 bg-[#fbfdfb] p-2.5 sm:p-5 min-w-0 space-y-4" data-testid={`ad-report-${adId}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="font-display text-lg sm:text-xl font-bold text-slate-900">{E.title}</div>
          <div className="text-xs text-slate-600 mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
            <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 font-semibold" data-testid={`ad-report-status-${adId}`}>{S.mgr.status[rep.status] || rep.status}</span>
            <span data-testid={`ad-report-period-${adId}`}>{period}</span>
          </div>
        </div>
        <EyeInfo adId={adId} rep={rep} E={E} locale={S.locale} />
      </div>
      <div className="space-y-2">
        <Presets adId={adId} preset={preset} pick={pick} E={E} />
        {preset === "custom" && <DateFilter adId={adId} draft={draft} setDraft={setDraft} onApply={apply} onClear={() => { setDraft({ from: "", to: "" }); pick("all"); }} active={filtered} S={S} />}
        {err && <div className="text-xs text-rose-600">{err}</div>}
      </div>
      {notLive && <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2" data-testid={`eye-not-live-${adId}`}>{E.notDelivered}</div>}
      <SummaryCards adId={adId} rep={rep} E={E} notDelivered={notLive} />
      {!rep.has_data ? (
        <div className="rounded-2xl bg-white border border-dashed border-slate-200 text-center text-sm text-slate-500 py-8 px-4" data-testid={`ad-report-no-data-${adId}`}>{filtered ? E.emptyRange : E.empty}</div>
      ) : (
        <>
          <PinCards adId={adId} rep={rep} E={E} />
          <div className="grid gap-3 lg:grid-cols-2">
            <WeeklyActivity adId={adId} rep={rep} S={S} />
            <ReachChart adId={adId} rep={rep} S={S} />
          </div>
        </>
      )}
      <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-emerald-100/70" data-testid={`eye-downloads-${adId}`}>
        <span className="text-[11px] text-slate-500 me-1">{E.downloads}:</span>
        <DownloadButton adId={adId} range={range} S={S} kind="pdf" />
        <DownloadButton adId={adId} range={range} S={S} kind="csv" />
        {canShare && <ShareReportPanel adId={adId} />}
      </div>
    </div>
  );
}
